import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';

import TableNoData from '../table-no-data';
import UserTableRow from '../user-table-row';
import UserTableHead from '../user-table-head';
import TableEmptyRows from '../table-empty-rows';
import UserTableToolbar from '../user-table-toolbar';
import { applyFilter, getComparator } from '../utils';
import Iconify from '../../../../components/iconify/iconify';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../../../../utils';
import { toast } from 'react-toastify';
import proxyServices from '../../../../services/proxyServices';
import { TableHeaderStyle } from '../../../../theme/palette';
import ProxyFormModal from '../../../../components/ProxyFormModal';

// ----------------------------------------------------------------------

const ROWS_PER_PAGE = 20;

export default function ProxyFoam() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [filterName, setFilterName] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProxyForm, setSelectedProxyForm] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = dataPaginated?.map((n) => n._id) || [];
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const dataFiltered = applyFilter({
    inputData: data,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const totalFiltered = dataFiltered.length;
  const dataPaginated = dataFiltered.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE
  );
  const notFound = !loading && totalFiltered === 0;

  const getData = () => {
    setLoading(true);
    proxyServices
      .getProxyForms()
      .then((res) => {
        setData(res?.data?.data || []);
      })
      .catch((err) => {
        toast.error(getErrorMessage(err));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getData();
  }, []);

  const handleDeleteRow = (row) => {
    proxyServices.deleteProxyForm(row._id).then(() => {
      toast.success("Proxy form deleted");
      getData();
    }).catch((err) => {
      toast.error(getErrorMessage(err));
    });
  };

  const handleViewRow = async (row) => {
    try {
      const response = await proxyServices.getProxyFormByID(row._id);
      setSelectedProxyForm(response.data.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error("Failed to load proxy form details");
      console.error("Error loading proxy form:", error);
    }
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedProxyForm(null);
  };

  const handleDownloadPDF = async (row) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const response = await fetch(`${baseURL}/admin/proxy-forms/${row._id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', errorText);
        throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
      }

      // Ensure we're getting binary data
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proxy_form_${row.member?.name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
    }
  };

  return (
    <div>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Stack>
          <Typography variant="h4">Proxy Forms</Typography>
          <div style={{
            backgroundColor: TableHeaderStyle.border,
            height: "2px"
          }} />
        </Stack>
      </Stack>

      <Card>
        <UserTableToolbar
          numSelected={selected.length}
          filterName={filterName}
          onFilterName={handleFilterByName}
        />

        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
          {loading ? "Loading..." : `Showing ${totalFiltered === 0 ? 0 : page * ROWS_PER_PAGE + 1}–${Math.min((page + 1) * ROWS_PER_PAGE, totalFiltered)} of ${totalFiltered} records`}
        </Typography>

        <TableContainer sx={{ overflow: { xs: 'auto', md: 'visible' } }}>
          <Table sx={{
            minWidth: { xs: 300, sm: 600, md: 800 },
            width: '100%',
            tableLayout: { md: 'fixed' }
          }}>
            <UserTableHead
              order={order}
              orderBy={orderBy}
              rowCount={dataPaginated?.length}
              numSelected={selected.length}
              onRequestSort={handleSort}
              onSelectAllClick={handleSelectAllClick}
              headLabel={[
                { id: '#', label: '#', width: '5%' },
                { id: 'member.name', label: 'Member Name', width: '22%' },
                { id: 'proxy.name', label: 'Proxy Name', width: '22%' },
                { id: 'meeting.date', label: 'Meeting Date', width: '18%' },
                { id: 'createdAt', label: 'Submitted Date', width: '22%' },
                { id: '', width: '5%' },
              ]}
            />
            <TableBody>
              {dataPaginated.map((row, index) => (
                <UserTableRow
                  key={row._id}
                  rowNumber={page * ROWS_PER_PAGE + index + 1}
                  memberName={row.member?.name}
                  proxyName={row.proxy?.name}
                  meetingDate={row.meeting?.date ? new Date(row.meeting.date).toLocaleDateString() : ''}
                  submittedDate={new Date(row.createdAt).toLocaleDateString()}
                  onDelete={() => handleDeleteRow(row)}
                  onView={() => handleViewRow(row)}
                  onDownloadPDF={() => handleDownloadPDF(row)}
                />
              ))}

              <TableEmptyRows height={77} emptyRows={0} />

              {!loading && notFound && <TableNoData query={filterName} />}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalFiltered}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
        />
      </Card>

      {/* View Modal */}
      <ProxyFormModal
        open={viewModalOpen}
        onClose={handleCloseModal}
        proxyForm={selectedProxyForm}
      />
    </div>
  );
}
